/**
 * Loop Subscriptions – Kündigungsflow Event Tracker
 * ---------------------------------------------------
 * Läuft ausschließlich auf den App-Proxy-Portal-Seiten
 * (fairmonto.shop/a/loop_subscriptions/...).
 *
 * ZWEI ERFASSUNGSWEGE, kombiniert:
 *
 * A) API-Interception (robust, hört auf Server-Calls, nicht auf UI):
 *    fetch()/XMLHttpRequest werden gepatcht, um Loop-API-Calls im
 *    Kündigungsflow mitzulesen. Liefert die "offiziellen" Zustände:
 *    Portal geöffnet, Grund gewählt, Angebot angenommen/abgelehnt,
 *    Kündigung bestätigt.
 *
 * B) DOM-Interaktions-Tracking (granularer, aber fragiler gegenüber
 *    Design-Änderungen): erfasst JEDEN Klick auf interaktive Elemente
 *    (auch wirkungslose - an/abwählen, Zurück, Auf-/Zuklappen), snapshot
 *    der sichtbaren Optionen bei jedem Schritt, Freitext-Nutzung in
 *    Kommentarfeldern, und Abbruch (Tab verlassen/Fenster schließen).
 *
 * Beide Wege laufen über dieselbe Normalisierung/denselben Webhook,
 * damit Auswertung einheitlich bleibt.
 *
 * Kein Cookie, kein Local-/SessionStorage, keine Drittanbieter-Bibliothek.
 * Alle Fehler werden geschluckt (try/catch) – das Script darf den
 * Kündigungsflow selbst niemals beeinflussen oder zum Absturz bringen.
 *
 * DATENSCHUTZ / FREITEXT:
 * Nur <textarea>-Elemente werden auf Inhalt geprüft (Kommentarfelder
 * sind praktisch immer Textareas). Zusätzlich greift eine Blacklist auf
 * name/id/autocomplete/type, die u.a. Zahlungs-, Adress-, Telefon-,
 * Email- und Passwortfelder ausschließt. Ziel: NUR die Freitext-
 * Kommentare aus dem Kündigungsflow selbst landen im Tracking.
 */
(function () {
  'use strict';

  if (window.__fmtPortalTrackerLoaded) return;
  window.__fmtPortalTrackerLoaded = true;

  // ---- KONFIGURATION -------------------------------------------------

  var CONFIG = {
    WEBHOOK_URL: 'https://automate.fairment.io/webhook/loop-portal-events',
    LOOP_HOSTS: ['apiv2.loopwork.co', 'api-customer.loopwork.co', 'api.loopwork.co'],
    // Felder, die NIE als Freitext erfasst werden, unabhängig vom Tag.
    // Prüft name/id/autocomplete/type/aria-label auf diese Muster.
    TEXT_INPUT_EXCLUDE_PATTERNS: [
      /password/i, /passwort/i, /card/i, /karte/i, /cvv/i, /cvc/i, /iban/i,
      /zip/i, /postal/i, /plz/i, /address/i, /adresse/i, /city/i, /stadt/i,
      /phone/i, /telefon/i, /tel$/i, /e[-_]?mail/i, /^name$/i, /vorname/i,
      /nachname/i, /firstname/i, /lastname/i,
    ],
    MAX_TEXT_LENGTH: 2000,
    MAX_ELEMENT_TEXT_LENGTH: 300,
    OPTIONS_SNAPSHOT_DEBOUNCE_MS: 400,
    // Mapping Shopify Selling-Plan-ID -> Name der Benefit-Page. Bestätigt
    // am 25.08. durch Tobi. Unbekannte IDs (nicht in dieser Liste) fallen
    // auf 'Standard Abo' zurück - das war laut Tobi "der Rest".
    // Zuverlässiger als SELLING_PLAN_TO_BENEFIT_PAGE: benefitId kommt
    // direkt aus der Response von subscriptionBenefit/inspect und
    // unterscheidet sich je Content-Variante (bestätigt am 27.08.: 7101 =
    // SuperBiom Standard, 6655 = Standard Abo). Vermutlich nur eine
    // Handvoll Werte insgesamt (einer pro Variante), nicht viele wie bei
    // den Selling-Plan-IDs (die sich pro Intervall/Rabatt unterscheiden,
    // ohne dass sich der Benefit-Page-Inhalt ändert).
    BENEFIT_ID_TO_BENEFIT_PAGE: {
      '7101': 'SuperBiom Standard',
      '7100': 'SuperBiom VIP',
      '6655': 'Standard Abo',
    },
    SELLING_PLAN_TO_BENEFIT_PAGE: {
      '692659421448': 'SuperBiom Standard',
      '692660306184': 'SuperBiom Standard',
      '692902592776': 'SuperBiom Standard',
      '692715323656': 'SuperBiom VIP',
    },
    DEFAULT_BENEFIT_PAGE_NAME: 'Standard Abo',
    // Container des Kündigungsflow-Popups. Bestätigt am 25.08. durch
    // Inspektion: alles läuft innerhalb von .loop-drawer.
    // Fallback-Kandidaten bleiben als Sicherheitsnetz, falls Loop die
    // Klasse mal umbenennt - .loop-drawer wird aber immer zuerst versucht.
    // .loop-drawer-content ist der breitere, tatsächlich umschließende
    // Container (bestätigt am 25.08.: .loop-drawer allein erfasst NICHT
    // die Reasons-Liste, die liegt als Geschwister-Struktur daneben).
    // .loop-drawer bleibt als Fallback, falls -content mal fehlt.
    CANCELLATION_FLOW_ROOT_SELECTORS: [
      '.loop-drawer-content',
      '.loop-drawer',
      '[role="dialog"]',
      '[class*="cancellation" i]',
      '[class*="cancel-flow" i]',
      '[class*="CancelFlow" i]',
      '[data-testid*="cancel" i]',
      '[id*="cancellation" i]',
    ],
  };

  // ---- LOOPPROPS AUSLESEN (einmalig, gecacht) -------------------------

  var reportedUnknownSellingPlan = false;
  function readLoopProps() {
    try {
      var props = window.loopProps || {};
      var customer = props.customer || {};
      var subscription = props.subscription || {};
      var lineItems = subscription.lineItems || [];
      var firstItem = lineItems[0] || {};
      // sellingPlanId bestimmt, welche der drei Benefit-Pages angezeigt
      // wird (nicht das Produkt selbst) - siehe SELLING_PLAN_TO_BENEFIT_PAGE.
      var sellingPlanId = firstItem.sellingPlanId != null ? String(firstItem.sellingPlanId) : null;
      var knownBenefitPage = sellingPlanId ? CONFIG.SELLING_PLAN_TO_BENEFIT_PAGE[sellingPlanId] : null;
      var benefitPageName = knownBenefitPage || CONFIG.DEFAULT_BENEFIT_PAGE_NAME;
      // Diagnose: sellingPlanId vorhanden, aber nicht in der Mapping-
      // Tabelle bekannt -> fällt sonst STILL auf "Standard Abo" zurück,
      // was falsche Daten erzeugen könnte, ohne dass es auffällt.
      // sellingPlanName/sellingPlanGroupName direkt mitschicken, damit
      // sofort erkennbar ist, um welchen Plan es geht - ohne erst in
      // Loop-Admin nachschauen zu müssen.
      if (sellingPlanId && !knownBenefitPage && !reportedUnknownSellingPlan) {
        reportedUnknownSellingPlan = true;
        emitNormalizedEvent({
          event_name: 'selling_plan_fallback_used',
          source: 'portal_frontend_diagnostic',
          raw: {
            selling_plan_id: sellingPlanId,
            selling_plan_name: firstItem.sellingPlanName || null,
            selling_plan_group_name: firstItem.sellingPlanGroupName || null,
          },
        });
      }
      return {
        email: (customer.email || '').toLowerCase().trim(),
        shopify_customer_id: customer.shopifyId != null ? String(customer.shopifyId) : null,
        shopify_subscription_id: subscription.shopifyId != null ? String(subscription.shopifyId) : null,
        loop_subscription_contract_id: subscription.id != null ? String(subscription.id) : null,
        product_handle: firstItem.productHandle || null,
        benefit_page_name: benefitPageName,
      };
    } catch (e) {
      return {
        email: '', shopify_customer_id: null, shopify_subscription_id: null,
        loop_subscription_contract_id: null, product_handle: null, benefit_page_name: null,
      };
    }
  }

  var identity = null;
  function getIdentity(cb) {
    // Erst cachen, wenn Email UND die Subscription-Felder vorhanden sind -
    // sonst kann ein zu früher Snapshot (loopProps lädt customer vor
    // subscription) dauerhaft mit null-IDs eingefroren werden.
    if (identity && identity.email && identity.loop_subscription_contract_id) {
      return cb(identity);
    }
    var attempts = 0;
    var timer = setInterval(function () {
      attempts++;
      var props = readLoopProps();
      var isComplete = props.email && props.loop_subscription_contract_id;
      if (isComplete || attempts > 20) {
        clearInterval(timer);
        identity = props;
        cb(identity);
      }
    }, 500);
  }

  var SESSION_ID = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random());

  // ---- GEMEINSAMER SENDEWEG -------------------------------------------

  function safeParseJSON(str) {
    try { return JSON.parse(str); } catch (e) { return null; }
  }

  function sendEvent(event) {
    try {
      fetch(CONFIG.WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(event),
        keepalive: true,
      }).catch(function () {});
    } catch (e) {
      // silent fail
    }
  }

  // Einheitlicher Event-Builder für BEIDE Erfassungswege (API + DOM).
  // fields: partielles Objekt, alles Fehlende wird null.
  // Alle event_names bekommen "cancellation_flow_" voran. Enthält der
  // Name schon "flow" (z.B. "flow_opened", "flow_closed"), nur
  // "cancellation_" voranstellen, um "cancellation_flow_flow_opened" zu
  // vermeiden.
  function prefixEventName(name) {
    // Kein cancellation_flow-Prefix, weil es unabhängig vom Kündigungs-
    // flow feuert (Kunde muss nie "Abo kündigen" klicken).
    if (name === 'portal_opened') return 'subscription_details_opened';
    if (name === 'selling_plan_fallback_used') return 'selling_plan_fallback_used';
    if (name === 'cancellation_confirmed') return 'cancellation_flow_confirmed';
    if (name.indexOf('flow') !== -1) return 'cancellation_' + name;
    return 'cancellation_flow_' + name;
  }

  // Bevorzugt die benefitId (zuverlässig, direkt von Loop geliefert) -
  // fällt auf die Selling-Plan-basierte Vermutung zurück, falls die
  // benefitId noch nicht eingetroffen ist (Timing) oder unbekannt.
  function resolveBenefitPageName(id) {
    try {
      if (lastKnownBenefitId && CONFIG.BENEFIT_ID_TO_BENEFIT_PAGE[lastKnownBenefitId]) {
        return CONFIG.BENEFIT_ID_TO_BENEFIT_PAGE[lastKnownBenefitId];
      }
    } catch (e) {}
    return id && id.benefit_page_name ? id.benefit_page_name : null;
  }

  function emitNormalizedEvent(fields) {
    getIdentity(function (id) {
      try {
        var event = {
          event_id: (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + '-' + Math.random()),
          event_ts: fields.event_ts_override || new Date().toISOString(),
          session_id: SESSION_ID,
          email: id.email || '',
          shopify_subscription_id: id.shopify_subscription_id,
          shopify_customer_id: id.shopify_customer_id,
          loop_subscription_contract_id: id.loop_subscription_contract_id,
          event_name: prefixEventName(fields.event_name || 'unknown'),
          // step_name-Override NUR für flow_opened (eigenes Flag), NICHT
          // für den Reasons-Screen - der zeigt echten DOM-Text.
          step_name: fields._step_name_needs_product_context
            ? 'Benefit Page'
            : (fields.step_name != null ? fields.step_name : null),
          element_text: fields.element_text != null ? fields.element_text : null,
          element_class: fields.element_class != null ? fields.element_class : null,
          options_shown: fields.options_shown != null ? fields.options_shown : null,
          text_input: fields.text_input != null ? fields.text_input : null,
          cancellation_action: fields.cancellation_action != null ? fields.cancellation_action : null,
          cancellation_reason: fields.cancellation_reason != null ? fields.cancellation_reason : null,
          // Bevorzugt: benefitId aus der subscriptionBenefit/inspect-
          // Response (zuverlässig, direkt von Loop). Fallback: die
          // Selling-Plan-basierte Vermutung, falls benefitId noch nicht
          // eingetroffen ist (Timing) oder unbekannt.
          benefit_page_viewed: fields._benefit_needs_product_context
            ? resolveBenefitPageName(id)
            : (fields.benefit_page_viewed != null ? fields.benefit_page_viewed : null),
          offer_viewed: fields.offer_viewed != null ? fields.offer_viewed : null,
          // NEU (Babett, 17.09.): sauber getrennt von offer_viewed - der
          // gewählte Kündigungsgrund im Klartext, nicht das Angebot.
          reason_selected: fields.reason_selected != null ? fields.reason_selected : null,
          treatment_opted: fields.treatment_opted != null ? fields.treatment_opted : null,
          source: fields.source || 'portal_frontend',
          raw: fields.raw !== undefined ? fields.raw : null,
        };
        sendEvent(event);
      } catch (e) {
        // niemals den Portal-Flow stören
      }
    });
  }

  // =====================================================================
  // WEG A: API-INTERCEPTION (fetch/XHR) - "offizielle" Zustandsänderungen
  // =====================================================================

  var ENDPOINT_RULES = [
    {
      match: 'subscriptionBenefit/inspect',
      build: function () {
        // Dieser Call feuert beim Klick auf "Abo kündigen" (Drawer öffnet
        // sich) - NICHT beim bloßen Aufruf der Abo-Detailseite. Echtes
        // "portal_opened" wird separat unten ausgelöst, sobald loopProps
        // beim Seitenaufruf verfügbar wird.
        // step_name kommt hier über die generische DOM-Erkennung
        // (getStepName), NICHT erzwungen - manche Selling-Pläne haben
        // KEINE Benefit-Page zugeordnet, dann springt der Flow direkt zur
        // Reasons-Liste. Die generische Erkennung unterscheidet das schon
        // korrekt (isBenefitPageVisible vs. isReasonSelectionScreenVisible).
        // Beide Ausgänge sind gültig - NICHT nur Benefit-Page als Erfolg
        // werten, sonst würde der Direkt-zu-Reasons-Fall fälschlich als
        // "nicht erkannt" gemeldet.
        scheduleScreenDetectionCheck('Benefit Page or Reasons Page', function () {
          var root = getFlowRoot();
          return isBenefitPageVisible(root) || isReasonSelectionScreenVisible(root);
        });
        return {
          event_name: 'flow_opened',
          _benefit_needs_product_context: true,
        };
      },
    },
    {
      match: 'cancellationFlow/upsertEvent',
      build: function (body) {
        var eventAction = body && body.eventAction ? String(body.eventAction) : null;
        var state = body && body.state ? String(body.state) : null;
        var eventType = body && body.eventType ? String(body.eventType) : null;
        // NOT_APPLICABLE zählt NICHT mehr grundsätzlich als Non-Retention -
        // bei state:"ACCEPTED" ist das der "Abonnement behalten"-Button auf
        // der Benefit-Page selbst (echtes Save, Loop gibt dafür keine
        // eigene eventAction). CANCEL bleibt immer ausgeschlossen.
        var NON_RETENTION_ACTIONS = ['CANCEL'];
        var isRetentionAction = eventAction && NON_RETENTION_ACTIONS.indexOf(eventAction) === -1;
        // eventType unterscheidet: "REASON" = eventId ist eine echte
        // cancellationReasonId (Babetts Liste, 132036 etc.). "BENEFIT"
        // (o.ä.) = generische erste Halte-Argument-Seite VOR der Grund-
        // auswahl, eventId liegt in einem anderen ID-Raum (z.B. 7100er)
        // und ist KEINE Reason-ID - würde sonst Babetts Join verfälschen.
        var isReasonContext = eventType === 'REASON';
        var eventIdStr = body && body.eventId != null ? String(body.eventId) : null;
        if (eventAction === 'CANCEL') {
          // Nicht jeder Grund hat einen Feedback-Screen - manche gehen
          // direkt zur Kündigung durch, ohne Freitext-Nachfrage. Beides
          // ist ein gültiger Ausgang: Feedback-Screen erschienen ODER der
          // Drawer ist inzwischen komplett verschwunden (Flow durchgelaufen).
          scheduleScreenDetectionCheck('Feedback Page or Skipped', function () {
            return isFeedbackScreenVisible(getFlowRoot()) || !isDrawerPresent();
          });
        }
        // Reiner Übergang "Benefit-Page verlassen, nichts akzeptiert"
        // (state REJECTED) - wird durch reasons_shown (DOM) abgedeckt.
        // NUR dieser Fall wird unterdrückt - state ACCEPTED (echtes
        // "Abonnement behalten"-Save) bleibt IMMER erhalten.
        if (!isReasonContext && eventAction === 'NOT_APPLICABLE' && state !== 'ACCEPTED') {
          scheduleScreenDetectionCheck('Reasons Page', function () {
            return isReasonSelectionScreenVisible(getFlowRoot());
          });
          return null;
        }
        return {
          event_name: 'benefit_response',
          cancellation_reason: isReasonContext ? eventIdStr : null,
          // Loops interne eventId für generische Benefit-Screens hat
          // keine Bedeutung außerhalb von Loop (keine Nachschlage-
          // möglichkeit). Es gibt drei Benefit-Pages je Produkt - das
          // Produkt-Handle (aus loopProps) wird stattdessen eingesetzt,
          // siehe _benefit_needs_product_context in emitNormalizedEvent.
          benefit_page_viewed: !isReasonContext ? eventIdStr : null,
          _benefit_needs_product_context: !isReasonContext,
          treatment_opted: eventAction,
          offer_viewed: body && body.eventActionId != null ? String(body.eventActionId) : null,
          cancellation_action: (state === 'ACCEPTED' && isRetentionAction) ? 'Saved' : null,
        };
      },
    },
    {
      match: 'incentive/inspect',
      build: function (body) {
        // Feuert tatsächlich, wenn Loop das passende Retention-Angebot
        // zum gewählten Grund anzeigt - nicht beim Radio-Klick selbst.
        // Bestätigt am 26.08. durch Tobi.
        return {
          event_name: 'retention_offer_shown',
          cancellation_reason: body && body.cancellationReasonId != null ? String(body.cancellationReasonId) : null,
        };
      },
    },
    {
      // WICHTIG (Fund von Babett, 16.09.): reiner Substring '/cancel' war
      // gefährlich unscharf - 'cancellationFlow/upsertEvent' enthält
      // ZUFÄLLIG ebenfalls die Zeichenfolge '/cancel' (als Präfix von
      // "cancellationFlow"). Die Regel-Reihenfolge schützt zwar VOR
      // diesem einen bekannten Fall (cancellationFlow/upsertEvent steht
      // weiter oben in der Liste), aber JEDER andere, uns unbekannte
      // Loop-Endpoint mit "cancel" im Namen hätte hier ebenfalls
      // fälschlich gematcht. 'v2/cancel' ist eindeutig auf den echten
      // finalen Bestätigungs-Call beschränkt.
      match: 'v2/cancel',
      build: function (body) {
        return {
          event_name: 'cancellation_confirmed',
          cancellation_action: 'Cancelled',
          cancellation_reason: body && body.reason_id != null ? String(body.reason_id) : null,
          // Der Freitext-Kommentar kommt hier schon strukturiert mit -
          // kein separates DOM-Tracking für DIESES Feld nötig.
          text_input: body && body.comment ? String(body.comment).slice(0, CONFIG.MAX_TEXT_LENGTH) : null,
        };
      },
    },
  ];

  function matchEndpoint(url) {
    for (var i = 0; i < ENDPOINT_RULES.length; i++) {
      if (url.indexOf(ENDPOINT_RULES[i].match) !== -1) return ENDPOINT_RULES[i];
    }
    return null;
  }

  function isLoopHost(url) {
    for (var i = 0; i < CONFIG.LOOP_HOSTS.length; i++) {
      if (url.indexOf(CONFIG.LOOP_HOSTS[i]) !== -1) return true;
    }
    return false;
  }

  function emitApiEvent(url, requestBody) {
    var rule = matchEndpoint(url);
    if (!rule) return;
    apiConfirmedFlowActive = true;
    scheduleFlowRootDiagnostic();
    // event_ts SOFORT einfangen (exakter Moment des API-Calls). Die
    // step_name-Erfassung selbst verzögern wir leicht, weil das DOM zu
    // diesem Zeitpunkt die zugehörige Seite (z.B. eine der drei Benefit-
    // Pages mit ihrem <strong>-Titel) oft noch nicht gerendert hat -
    // der Call geht raus, BEVOR die Antwort da ist und die UI reagiert.
    var capturedTs = new Date().toISOString();
    // Heading VOR der Aktion merken - danach pollen, bis es sich ändert
    // (DOM hat auf den nächsten Screen umgeschaltet) statt einer festen
    // Wartezeit. 150ms-Intervall, max. 6 Versuche (~900ms gesamt).
    var stepNameBefore = getStepName();
    try {
      var builtResult = rule.build(requestBody);
      if (builtResult === null) return; // Rule signalisiert bewusstes Unterdrücken
      var extra = builtResult || {};
      if (extra.event_name === 'flow_opened') {
        // API-bestätigtes Öffnen erkannt - der DOM-Fallback (siehe
        // checkDrawerLifecycle) muss dann NICHT zusätzlich feuern.
        apiFlowOpenedFired = true;
      }
      // SOFORT (synchron), nicht erst nach Polling: incentive/inspect
      // feuert laut Tobi NUR, wenn tatsächlich ein Angebot geladen wird -
      // die reine Existenz des Calls reicht als Signal. Bleibt gesetzt,
      // bis ein neuer Grund gewählt wird (siehe ui_click-Handler). Kein
      // Wettlauf mit dem DOM-Mutation-Timer mehr nötig.
      if (extra.event_name === 'retention_offer_shown') {
        // Kein eigenes Event von hier - nur Kontext setzen. Das EINZIGE
        // Event kommt vom DOM-Mechanismus (scheduleOptionsSnapshot),
        // sobald der Angebots-Inhalt tatsächlich sichtbar ist, angereichert
        // mit der hier gelieferten cancellationReasonId.
        currentScreenIsRetentionOffer = true;
        pendingRetentionOfferReasonId = extra.cancellation_reason;
        return;
      }
      var usesDomStepName = !extra._step_name_needs_product_context;
      var attempts = 0;
      var maxAttempts = 10; // 10 x 150ms = 1.5s Gesamtbudget
      var poll = function () {
        attempts++;
        var reasonRadiosVisible = usesDomStepName && isReasonSelectionScreenVisible();
        var currentStepName = usesDomStepName ? getStepName() : null;
        var headingChanged = !usesDomStepName || currentStepName !== stepNameBefore;
        // Bei benefit_page_viewed-relevanten Events NICHT vorzeitig
        // abschließen, nur weil sich der DOM-Zustand schon geändert hat -
        // erst warten, bis die asynchrone benefitId-Response da ist (oder
        // das Zeitbudget aufgebraucht ist), sonst greift fälschlich die
        // alte Selling-Plan-Vermutung statt der zuverlässigen benefitId.
        var awaitingBenefitId = extra._benefit_needs_product_context && !lastKnownBenefitId;
        var done = ((reasonRadiosVisible || headingChanged) && !awaitingBenefitId) || attempts >= maxAttempts;
        if (done) {
          // Radios sichtbar -> fester Name, unabhängig davon ob Loop dafür
          // überhaupt einen Heading/<strong>-Text zeigt (tut es laut Tobi
          // bei der Reasons-Liste nicht).
          var resolvedStepName = reasonRadiosVisible ? 'Reasons Page' : currentStepName;
          emitNormalizedEvent({
            event_ts_override: capturedTs,
            event_name: extra.event_name,
            step_name: extra.step_name || resolvedStepName,
            element_text: extra.element_text,
            element_class: extra.element_class,
            options_shown: extra.options_shown,
            text_input: extra.text_input,
            cancellation_action: extra.cancellation_action,
            cancellation_reason: extra.cancellation_reason,
            benefit_page_viewed: extra.benefit_page_viewed,
            _benefit_needs_product_context: extra._benefit_needs_product_context,
            _step_name_needs_product_context: extra._step_name_needs_product_context,
            offer_viewed: extra.offer_viewed,
            reason_selected: extra.reason_selected,
            treatment_opted: extra.treatment_opted,
            source: 'portal_frontend',
            raw: { endpoint: url, request_body: requestBody },
          });
        } else {
          setTimeout(poll, 150);
        }
      };
      setTimeout(poll, 150);
    } catch (e) {
      // silent fail
    }
  }

  // Von subscriptionBenefit/inspect-Response gelieferte benefitId -
  // zuverlässiger als die Selling-Plan-basierte Vermutung. Wird
  // asynchron nach Antwort des EINEN bekannten Calls gesetzt (keine
  // generelle Response-Interception für alle Endpoints).
  var lastKnownBenefitId = null;
  var reportedUnknownBenefitId = false;

  // Gemeinsame Verarbeitung für BEIDE Pfade (fetch und XHR) - verhindert,
  // dass die Logik auseinanderläuft, wenn wir sie später mal anpassen.
  function processBenefitInspectData(data) {
    try {
      var benefitId = data && data.data && data.data.benefitId != null ? String(data.data.benefitId) : null;
      if (!benefitId) return;
      lastKnownBenefitId = benefitId;
      if (!CONFIG.BENEFIT_ID_TO_BENEFIT_PAGE[benefitId] && !reportedUnknownBenefitId) {
        reportedUnknownBenefitId = true;
        emitNormalizedEvent({
          event_name: 'benefit_id_fallback',
          source: 'portal_frontend_diagnostic',
          raw: { benefit_id: benefitId },
        });
      }
    } catch (e) {
      // silent fail
    }
  }

  function handleBenefitInspectResponse(responsePromise) {
    try {
      responsePromise
        .then(function (res) { return res.clone().json(); })
        .then(processBenefitInspectData)
        .catch(function () {});
    } catch (e) {
      // silent fail
    }
  }

  var originalFetch = window.fetch;
  if (originalFetch) {
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (isLoopHost(url) && init && init.body) {
          var body = safeParseJSON(init.body);
          emitApiEvent(url, body);
        }
      } catch (e) {
        // silent fail
      }
      var fetchPromise = originalFetch.apply(this, arguments);
      try {
        if (url && url.indexOf('subscriptionBenefit/inspect') !== -1) {
          // Vor jedem NEUEN Call zurücksetzen - sonst würde ein alter Wert
          // aus einer früheren Drawer-Öffnung (z.B. beim erneuten Öffnen
          // in derselben Sitzung) fälschlich weiterverwendet, falls diese
          // neue Antwort eine andere/keine benefitId liefert.
          lastKnownBenefitId = null;
          handleBenefitInspectResponse(fetchPromise);
        }
      } catch (e) {
        // silent fail
      }
      return fetchPromise;
    };
  }

  var OriginalOpen = XMLHttpRequest.prototype.open;
  var OriginalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__fmtUrl = url;
    return OriginalOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    try {
      if (this.__fmtUrl && isLoopHost(this.__fmtUrl)) {
        var parsedBody = typeof body === 'string' ? safeParseJSON(body) : null;
        emitApiEvent(this.__fmtUrl, parsedBody);
      }
      if (this.__fmtUrl && this.__fmtUrl.indexOf('subscriptionBenefit/inspect') !== -1) {
        // Vor jedem NEUEN Call zurücksetzen - sonst würde ein alter Wert
        // aus einer früheren Drawer-Öffnung fälschlich weiterverwendet.
        lastKnownBenefitId = null;
        this.addEventListener('load', function () {
          processBenefitInspectData(safeParseJSON(this.responseText));
        });
      }
    } catch (e) {
      // silent fail
    }
    return OriginalSend.apply(this, arguments);
  };

  // =====================================================================
  // WEG B: DOM-INTERAKTIONS-TRACKING - granularer, aber design-abhängig
  // =====================================================================

  // -- Hilfsfunktionen zum Auslesen von sichtbarem Text/IDs -------------

  // offsetParent allein reicht nicht: Loop hält offenbar mehrere Schritte
  // gleichzeitig im DOM und blendet inaktive nur visuell aus (z.B. per
  // visibility/height statt display:none) - solche Elemente können
  // trotzdem einen offsetParent haben. Zusätzlich echte Render-Größe
  // prüfen, um "im DOM, aber nicht wirklich sichtbar" auszuschließen.
  // Loop hält vermutlich alle Drawer-Schritte gleichzeitig im DOM und
  // schiebt inaktive nur per CSS-transform (z.B. translateX) aus dem
  // sichtbaren Bereich - solche Elemente haben weiterhin width/height > 0
  // (transform ändert nur die Position, nicht die Layout-Box-Größe).
  // Deshalb zusätzlich prüfen, ob die tatsächliche Position innerhalb
  // des Viewports liegt.
  function isVisible(el) {
    try {
      if (!el) return false;
      var rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return false;
      var inViewport = rect.bottom > 0 && rect.top < window.innerHeight &&
        rect.right > 0 && rect.left < window.innerWidth;
      return inViewport;
    } catch (e) {
      return false;
    }
  }

  // Findet den Container des Kündigungsflow-Popups. FAIL-CLOSED: wird
  // kein passender Container gefunden, liefert die Funktion null, und
  // JEDER DOM-Listener bricht dann ab, statt ersatzweise document-weit
  // zu tracken. Das verhindert Rauschen von Header/Navigation/Cookie-
  // Banner, kostet uns aber Daten, solange der echte Selektor fehlt.
  var flowRootMissingReported = false;
  // Bekannte Loop-Hosts, aber Calls ohne passende Regel - einmalig pro
  // Endpoint melden (nicht pro Aufruf), damit neue/geänderte Loop-
  // Endpoints früh auffallen statt unbemerkt zu verschwinden.
  // Screen-spezifische Erkennungs-Diagnose: nach einem bekannten
  // Vorläufer-Ereignis (z.B. "Grund angeklickt") kurz warten und prüfen,
  // ob die zugehörige Erkennungsfunktion tatsächlich true liefert. Falls
  // nicht, einmalig pro Screen melden - deckt gezielt einzelne kaputte
  // Detektoren ab (nicht nur den kompletten Root-Fund wie
  // flow_root_not_found).
  var reportedMissingScreens = {};
  // Statt einmalig nach fester Wartezeit zu prüfen: wiederholt pollen, bis
  // entweder erkannt wird ODER das Zeitbudget aufgebraucht ist. Schnelle
  // Fälle werden sofort bestätigt (kein unnötiges Warten), langsame Fälle
  // (z.B. Safari auf schwacher Verbindung) bekommen trotzdem genug Zeit,
  // bevor überhaupt eine Diagnose gemeldet wird - kein Kompromiss zwischen
  // "schnell" und "robust" nötig.
  function scheduleScreenDetectionCheck(screenName, checkFn) {
    var attempts = 0;
    var maxAttempts = 15; // 15 x 400ms = 6s Gesamtbudget im Worst Case
    var intervalMs = 400;
    var poll = function () {
      attempts++;
      try {
        if (checkFn()) return; // erkannt, alles gut - fertig
        if (attempts >= maxAttempts) {
          if (reportedMissingScreens[screenName]) return; // schon gemeldet
          reportedMissingScreens[screenName] = true;
          emitNormalizedEvent({
            event_name: 'screen_not_detected',
            step_name: screenName,
            source: 'portal_frontend_dom_diagnostic',
          });
          return;
        }
        setTimeout(poll, intervalMs);
      } catch (e) {
        // silent fail
      }
    };
    setTimeout(poll, intervalMs);
  }
  var flowRootEverFound = false;
  var apiConfirmedFlowActive = false; // wird true, sobald ein echter Loop-API-Call im Kündigungsflow gefeuert hat
  // Wenn retention_offer_shown (API) feuert, werden die Button-Texte
  // direkt eingefangen und lastOptionsKey vorab gesetzt - das verhindert
  // das doppelte DOM-Event, ohne eine separate Zeitfenster-Variable.

  // WICHTIG: Jeder Schritt im Flow ist sein EIGENER .loop-drawer (mit
  // eigener id, z.B. "loop-cp-drawer-id-subscription-benefits" vs.
  // "loop-cp-drawer-id-cancel-subscription-cancel-flow"). Beim Klick auf
  // "Weiter" wird ein NEUER Drawer hinzugefügt, der alte bleibt im DOM.
  // document.querySelector() gäbe den ERSTEN (ältesten) Treffer zurück -
  // wir wollen aber den LETZTEN (aktuellsten Schritt).
  function getFlowRoot() {
    try {
      for (var i = 0; i < CONFIG.CANCELLATION_FLOW_ROOT_SELECTORS.length; i++) {
        var matches = document.querySelectorAll(CONFIG.CANCELLATION_FLOW_ROOT_SELECTORS[i]);
        if (matches.length) {
          flowRootEverFound = true;
          return matches[matches.length - 1];
        }
      }
    } catch (e) {}
    return null;
  }

  // Diagnose ENTKOPPELT von reaktiven Checks (Klicks/Mutations) - sonst
  // feuert sie fälschlich in dem kurzen Moment, bevor .loop-drawer nach
  // dem Öffnen tatsächlich ins DOM eingefügt wurde. Statt bei jedem
  // getFlowRoot()-Aufruf zu prüfen, einmalig mit 3s Puffer nachschauen,
  // ob der Container in der Zwischenzeit doch noch gefunden wurde.
  function scheduleFlowRootDiagnostic() {
    if (flowRootMissingReported) return;
    setTimeout(function () {
      if (flowRootMissingReported || flowRootEverFound) return;
      getFlowRoot(); // letzter Versuch, kurz vor der Diagnose
      if (flowRootEverFound) return;
      flowRootMissingReported = true;
      emitNormalizedEvent({
        event_name: 'flow_root_not_found',
        source: 'portal_frontend_dom_diagnostic',
      });
    }, 3000);
  }

  function getElementText(el) {
    try {
      var text = '';
      if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
        // Für Inputs selbst lieber das zugehörige Label als den Wert
        // (Wert könnte PII sein, z.B. bei Freitextfeldern).
        // Label kann Elternelement sein (closest) ODER per for/id-Attribut
        // verknüpft (Geschwister-Element) - genau letzteres ist bei den
        // Reason-Radios im Kündigungsflow der Fall.
        var labelled = el.closest('label');
        if (!labelled && el.id) {
          try { labelled = document.querySelector('label[for="' + el.id + '"]'); } catch (e2) {}
        }
        text = labelled ? (labelled.innerText || labelled.textContent || '') : '';
        if (!text && el.getAttribute) text = el.getAttribute('aria-label') || '';
      } else {
        text = el.innerText || el.textContent || '';
        // Icon-only-Buttons (z.B. das X zum Schließen) haben oft keinen
        // sichtbaren Text - dann auf aria-label/title/SVG-<title> ausweichen.
        if (!text && el.getAttribute) {
          text = el.getAttribute('aria-label') || el.getAttribute('title') || '';
        }
        if (!text && el.querySelector) {
          var svgTitle = el.querySelector('title');
          if (svgTitle) text = svgTitle.textContent || '';
        }
      }
      if (!text && el.closest) {
        var ancestor = el.closest('label, button, [role="button"], li, a');
        if (ancestor) {
          text = ancestor.innerText || ancestor.textContent ||
            ancestor.getAttribute('aria-label') || ancestor.getAttribute('title') || '';
        }
      }
      text = (text || '').trim().replace(/\s+/g, ' ');
      return text ? text.slice(0, CONFIG.MAX_ELEMENT_TEXT_LENGTH) : null;
    } catch (e) {
      return null;
    }
  }

  function getElementId(el) {
    try {
      return el.id || el.name || el.getAttribute('data-testid') || el.getAttribute('data-id') ||
        (el.tagName === 'INPUT' && el.type === 'radio' ? el.value : null) || null;
    } catch (e) {
      return null;
    }
  }

  // Heuristik: letzte sichtbare Überschrift INNERHALB des Kündigungsflow-
  // Containers als "aktueller Schritt". Fallback-Kette, weil Loops
  // Angebots-Texte ihre Titel teils in <strong> statt h1-h3 packen (siehe
  // Babetts cancellation_message_options-HTML). lastKnownStepName wird
  // gecacht, damit step_name nach der ersten Erkennung nie wieder null
  // zurückfällt, auch wenn ein Zwischenschritt kurzzeitig keine erkennbare
  // Überschrift zeigt.
  var lastKnownStepName = null;
  // Merkt sich den Klartext des zuletzt angeklickten Kündigungsgrunds
  // (aus der Reasons-Liste) - der Folge-Screen (Reason Custom Actions)
  // hat selbst keine ID/Nummer, nur eine wechselnde Marketing-Überschrift.
  var lastClickedReasonText = null;
  // Dauerhafte Markierung statt Timing-Wettlauf zwischen zwei Timern:
  // incentive/inspect feuert NUR, wenn tatsächlich ein Angebot geladen
  // wird (bestätigt von Tobi) - die reine Existenz des Calls reicht als
  // Signal. Bleibt gesetzt, bis ein neuer Grund gewählt wird.
  var currentScreenIsRetentionOffer = false;
  // Von incentive/inspect gelieferte Reason-ID, die ins EINZIGE (vom DOM
  // ausgelöste) retention_offer_shown-Event eingebettet wird - der API-
  // Call selbst sendet dafür kein eigenes Event mehr.
  var pendingRetentionOfferReasonId = null;
  // Primärer Angebotsname (z.B. "Mit 25% weitermachen") - der erste
  // Button-Text auf dem Retention-Offer-Screen. ANNAHME (bestätigt an
  // den bisher beobachteten Beispielen): Loop listet den Annahme-Button
  // immer VOR dem Ablehnungs-Button. Falls sich das als falsch erweist,
  // hier nachschärfen.
  var currentOfferName = null;
  // Erkennt, ob die Reasons-Liste sichtbar ist (Radio-Buttons im Flow-
  // Container) - unabhängig davon, ob Loop dafür einen Heading-Text zeigt.
  // Bestätigt am 25.08. durch Tobi: Die Reasons-Liste hat kein Heading,
  // aber eine feste Klasse, die zuverlässig erkennbar ist.
  function isReasonSelectionScreenVisible(root) {
    try {
      root = root || getFlowRoot();
      if (!root) return false;
      var el = root.querySelector('.loop-cancellation-flow-drawer-choice');
      return isVisible(el);
    } catch (e) {}
    return false;
  }

  // Erkennung über die id des äußeren .loop-drawer (Loops eigene, stabile
  // Namensgebung, z.B. "loop-cp-drawer-id-subscription-benefits") - robuster
  // als der wechselnde Marketing-Inhalt selbst.
  // Erkennung über die id des äußeren .loop-drawer - dritter eigener
  // Drawer, id enthält "cancel-reason" (bestätigt am 26.08.), zeigt sich
  // je nach gewähltem Kündigungsgrund und hat Buttons statt Radios.
  function isReasonCustomActionsVisible(root) {
    try {
      root = root || getFlowRoot();
      if (!root) return false;
      var drawerEl = root.classList && root.classList.contains('loop-drawer')
        ? root
        : (root.closest ? root.closest('.loop-drawer') : null);
      if (!drawerEl) return false;
      // Zwei beobachtete ID-Varianten für diesen Screen:
      // "cancel-subscription-cancel-reason" (generisch) UND
      // "cancellation-reason" (wenn ein konkretes Angebot geladen ist).
      // Robuster Check statt exaktem String: beide Substrings "cancel"
      // UND "reason" müssen enthalten sein - schließt die Reasons-Liste
      // ("cancel-flow", kein "reason") und den Feedback-Screen
      // ("cancel-subscription", kein "reason") sicher aus.
      var id = drawerEl.id || '';
      if (id.indexOf('cancel') !== -1 && id.indexOf('reason') !== -1) {
        return true;
      }
      // Fallback, falls Loop ein drittes/unbekanntes ID-Schema einführt:
      // Ausschluss-Prüfung - keiner der drei anderen bekannten Screens
      // trifft zu, aber es gibt einen eigenen Drawer mit sichtbaren,
      // klickbaren Buttons/Links (die einzigen Elemente, die dieser
      // Screen laut bisheriger Beobachtung immer hat).
      // WICHTIG: hier NICHT isFeedbackScreenVisible() aufrufen - deren
      // eigener Fallback ruft diese Funktion auf, das gäbe eine
      // Endlosschleife. Direkt inline auf Textarea prüfen stattdessen.
      if (isBenefitPageVisible(root) || isReasonSelectionScreenVisible(root)) {
        return false;
      }
      if (drawerEl.querySelector('textarea')) {
        return false; // wahrscheinlich der Feedback-Screen, nicht dieser
      }
      var clickable = drawerEl.querySelectorAll('button, [role="button"], a');
      for (var i = 0; i < clickable.length; i++) {
        if ((clickable[i].className || '').indexOf('loop-drawer-close-btn') !== -1) continue;
        if (isVisible(clickable[i])) return true;
      }
    } catch (e) {}
    return false;
  }

  // Vierter Drawer (Feedback-Screen nach "trotzdem kündigen"). EXAKTER
  // Id-Abgleich, KEIN Substring-Check - "cancel-subscription" ist
  // Präfix der beiden anderen IDs (...-cancel-flow, ...-cancel-reason)
  // und würde bei bloßem indexOf() fälschlich auch auf die matchen.
  function isFeedbackScreenVisible(root) {
    try {
      root = root || getFlowRoot();
      if (!root) return false;
      var drawerEl = root.classList && root.classList.contains('loop-drawer')
        ? root
        : (root.closest ? root.closest('.loop-drawer') : null);
      if (drawerEl && drawerEl.id === 'loop-cp-drawer-id-cancel-subscription') {
        return true;
      }
      // Fallback, falls Loop die id mal ändert: der Feedback-Screen ist
      // der EINZIGE der vier Screens mit einem sichtbaren Freitextfeld -
      // Benefit-Page, Reasons-Liste und Reason-Custom-Actions haben nie
      // eine Textarea. Zusätzliche Ausschlussprüfung der anderen drei
      // Screens als Vorsichtsmaßnahme, falls sich das mal ändert.
      var textarea = root.querySelector('textarea');
      if (textarea && isVisible(textarea) &&
        !isBenefitPageVisible(root) &&
        !isReasonSelectionScreenVisible(root) &&
        !isReasonCustomActionsVisible(root)) {
        return true;
      }
    } catch (e) {}
    return false;
  }

  function isBenefitPageVisible(root) {
    try {
      root = root || getFlowRoot();
      if (!root) return false;
      var drawerEl = root.classList && root.classList.contains('loop-drawer')
        ? root
        : (root.closest ? root.closest('.loop-drawer') : null);
      if (drawerEl && (drawerEl.id || '').indexOf('subscription-benefits') !== -1) {
        return true;
      }
      // Zusätzliche, unabhängige Erkennung über den bekannten Marketing-
      // Inhalt, falls die id-basierte Prüfung aus irgendeinem Grund nicht
      // greift (z.B. abweichende DOM-Struktur).
      return !!root.querySelector('[class*="fairment-benefits"]');
    } catch (e) {}
    return false;
  }

  // Reiner Heading-Scrape ohne die festen Overrides (Benefit Page etc.) -
  // nutzbar, wenn man den ECHTEN Marketing-Text braucht (z.B. um zu
  // wissen, für welchen Grund der Reason-Custom-Actions-Screen gerade
  // gilt), nicht den generischen Screen-Namen.
  function scrapeHeadingText(root) {
    try {
      if (!root) return null;
      var candidates = root.querySelectorAll('h1, h2, h3, [role="heading"], strong, b');
      for (var i = 0; i < candidates.length; i++) {
        var el = candidates[i];
        if (!isVisible(el)) continue;
        var t = (el.innerText || el.textContent || '').trim();
        if (t && t.length > 2) return t.slice(0, 200);
      }
    } catch (e) {}
    return null;
  }

  function getStepName() {
    try {
      var root = getFlowRoot();
      if (root) {
        // Benefit-Page: Marketing-Inhalt wechselt (Standard/VIP/Standard
        // Abo haben je eigene Überschrift) - fester Name statt der
        // wechselnden Marketing-Headline, gilt für ALLE Aufrufer.
        if (isBenefitPageVisible(root)) {
          lastKnownStepName = 'Benefit Page';
          return lastKnownStepName;
        }
        // Reason-Custom-Actions-Screen (dritter Drawer, je nach gewähltem Grund) - Marketing-
        // Text wechselt ebenfalls, fester Name statt Überschrift.
        if (isReasonCustomActionsVisible(root)) {
          lastKnownStepName = 'Reason Custom Actions';
          return lastKnownStepName;
        }
        // Feedback-Screen (vierter Drawer, nach "trotzdem kündigen").
        if (isFeedbackScreenVisible(root)) {
          lastKnownStepName = 'Feedback Page';
          return lastKnownStepName;
        }
        // Reasons-Liste hat kein Heading - Sonderregel direkt hier, damit
        // ALLE Aufrufer (ui_click, options_shown, Freitext, Abbruch, nicht
        // nur die API-Event-Polling-Logik) korrekt "Reason Selection"
        // bekommen, statt auf einen alten gecachten Heading zurückzufallen.
        if (isReasonSelectionScreenVisible(root)) {
          lastKnownStepName = 'Reasons Page';
          return lastKnownStepName;
        }
        var scraped = scrapeHeadingText(root);
        if (scraped) {
          lastKnownStepName = scraped;
          return lastKnownStepName;
        }
      }
    } catch (e) {}
    return lastKnownStepName;
  }

  // -- B1: Jeder Klick auf ein interaktives Element ----------------------

  document.addEventListener('click', function (e) {
    try {
      var root = getFlowRoot();
      if (!root) return; // kein Flow offen, ignorieren
      if (!root.contains(e.target)) {
        // Klick AUSSERHALB des Drawers, während der Flow offen war -
        // möglicher Backdrop-Dismiss (Klick neben den Drawer schließt
        // ihn). Nur nach kurzer Verzögerung prüfen, ob der Drawer
        // dadurch tatsächlich verschlossen wurde - sonst war es nur ein
        // harmloser Klick irgendwo auf der Seite, kein echter Dismiss.
        // WICHTIG: step_name/benefit_page_viewed HIER, live, berechnen -
        // solange der Drawer noch existiert - nicht erst im Timeout
        // danach, wenn er schon weg ist.
        var stepNameAtClick = getStepName();
        var wasBenefitPage = isBenefitPageVisible(root);
        setTimeout(function () {
          if (!isDrawerPresent()) {
            emitNormalizedEvent({
              event_name: 'ui_click',
              step_name: stepNameAtClick,
              element_text: 'X',
              element_class: 'backdrop-dismiss',
              _benefit_needs_product_context: wasBenefitPage,
              source: 'portal_frontend_dom',
              raw: { tag: 'BACKDROP', type: null },
            });
          }
        }, 100);
        return;
      }
      var el = e.target;
      var interactive = el.closest && el.closest(
        'button, [role="button"], input[type="radio"], input[type="checkbox"], a, li[onclick], [role="radio"], [role="checkbox"]'
      );
      if (!interactive || !root.contains(interactive)) return;
      var clickedClass = interactive.className || '';
      var isCloseBtn = clickedClass.indexOf('loop-drawer-close-btn') !== -1;
      var onCustomActionsScreen = isReasonCustomActionsVisible(root);
      var resolvedElementText = isCloseBtn ? 'X' : getElementText(interactive);
      // Klick auf einen Kündigungsgrund merken - der Folge-Screen (Reason
      // Custom Actions) hat selbst keine ID, nur eine Marketing-
      // Überschrift. Der tatsächlich gewählte Grund ist der verlässlichere
      // Identifikator dafür, WELCHER Reason-Custom-Actions-Screen das ist.
      if (interactive.closest && interactive.closest('.loop-cancellation-flow-drawer-choice')) {
        lastClickedReasonText = resolvedElementText;
        // Neuer Grund gewählt -> Angebots-Prüfung startet neu, alte
        // Markierung darf nicht für den neuen Grund weitergelten.
        currentScreenIsRetentionOffer = false;
        pendingRetentionOfferReasonId = null;
        currentOfferName = null;
        // WICHTIG: Diagnose-Check NICHT hier - Radio-Auswahl allein löst
        // noch keinen Screen-Wechsel aus. Erst der separate "Weiter"-
        // Klick im Footer der Reasons-Liste tut das (siehe unten).
      } else if (isReasonSelectionScreenVisible(root) && interactive.tagName === 'BUTTON') {
        // Klick auf einen Button auf der Reasons-Liste, der NICHT der
        // Radio selbst ist -> vermutlich der "Weiter"-Klick, der den
        // Screen-Wechsel tatsächlich auslöst.
        scheduleScreenDetectionCheck('Reason Custom Actions', function () {
          return isReasonCustomActionsVisible(getFlowRoot());
        });
      }
      emitNormalizedEvent({
        event_name: 'ui_click',
        step_name: getStepName(),
        element_text: resolvedElementText,
        element_class: clickedClass || null,
        offer_viewed: (onCustomActionsScreen && currentScreenIsRetentionOffer) ? currentOfferName : null,
        reason_selected: onCustomActionsScreen ? lastClickedReasonText : null,
        _benefit_needs_product_context: isBenefitPageVisible(root),
        source: 'portal_frontend_dom',
        raw: {
          tag: interactive.tagName,
          type: interactive.type || null,
          aria_label: interactive.getAttribute ? interactive.getAttribute('aria-label') : null,
          class: clickedClass || null,
        },
      });
    } catch (e) {
      // silent fail
    }
  }, true);

  // -- B1b: ESC-Taste schließt den Drawer genauso wie der X-Button -----
  // Erzeugt dasselbe ui_click-Signal, damit ESC/Backdrop/X analytisch
  // gleich behandelt werden (alle drei sind "Flow ohne Entscheidung
  // verlassen").
  document.addEventListener('keydown', function (e) {
    try {
      if (e.key !== 'Escape' && e.keyCode !== 27) return;
      var root = getFlowRoot();
      if (!root) return; // kein Flow offen
      emitNormalizedEvent({
        event_name: 'ui_click',
        step_name: getStepName(),
        element_text: 'X',
        element_class: 'esc-key',
        _benefit_needs_product_context: isBenefitPageVisible(root),
        source: 'portal_frontend_dom',
        raw: { tag: 'KEYDOWN', type: 'Escape' },
      });
    } catch (e) {
      // silent fail
    }
  }, true);

  // -- B2: Snapshot der sichtbaren Optionen bei jedem Schrittwechsel ----

  var lastOptionsKey = null;
  // Feedback-Screen hat weder Radios noch Buttons (nur ein Freitextfeld) -
  // eigene Dedup-Variable, damit "Screen wurde gesehen" trotzdem als
  // eigenes Event erfasst wird, unabhängig davon ob der Kunde tippt/klickt.
  var lastFeedbackShownFor = null;
  var optionsSnapshotTimer = null;

  function scheduleOptionsSnapshot() {
    clearTimeout(optionsSnapshotTimer);
    optionsSnapshotTimer = setTimeout(function () {
      try {
        var root = getFlowRoot();
        if (!root) return;
        // Feedback-Screen hat kein Radio/Button-Muster zum Snapshotten -
        // eigenes, einfaches "Screen wurde gesehen"-Event, damit dieser
        // Schritt nicht komplett unsichtbar bleibt, falls der Kunde nur
        // schaut, ohne zu tippen oder zu klicken.
        if (isFeedbackScreenVisible(root)) {
          var feedbackKey = 'feedback:' + (lastClickedReasonText || '');
          if (feedbackKey !== lastFeedbackShownFor) {
            lastFeedbackShownFor = feedbackKey;
            emitNormalizedEvent({
              event_name: 'feedback_shown',
              step_name: 'Feedback Page',
              reason_selected: lastClickedReasonText,
              source: 'portal_frontend_dom',
            });
          }
          return;
        }
        var isCustomActionsScreen = isReasonCustomActionsVisible(root);
        // Reasons-Liste: Radios. Reason-Custom-Actions-Screen: Buttons/Links statt Radios
        // (z.B. "Intervall ändern", "Nächste Lieferung verschieben").
        var optionEls = isCustomActionsScreen
          ? root.querySelectorAll('button, [role="button"], a')
          : root.querySelectorAll('input[type="radio"], input[type="checkbox"], [role="radio"], [role="checkbox"]');
        if (!optionEls.length) return;
        var options = [];
        for (var i = 0; i < optionEls.length; i++) {
          if (!isVisible(optionEls[i])) continue;
          // Auf der Reason-Custom-Actions-Screen den Schließen-Button nicht als Option werten.
          if (isCustomActionsScreen && (optionEls[i].className || '').indexOf('loop-drawer-close-btn') !== -1) continue;
          var text = getElementText(optionEls[i]);
          if (text) options.push(text);
        }
        if (!options.length) return;
        // Schlüssel um den gewählten Grund erweitern - sonst würden zwei
        // VERSCHIEDENE Gründe mit identischen Button-Texten (z.B. beide
        // nur "Intervall ändern"/"Nächste Lieferung verschieben") fälschlich
        // als "schon gesendet" gelten, nur weil der Text gleich ist.
        var key = (isCustomActionsScreen ? 'reason:' + (lastClickedReasonText || '') + '|' : '') + options.join('|');
        if (key === lastOptionsKey) return; // gleicher Screen, nicht doppelt senden
        lastOptionsKey = key;
        // Reasons-Liste und Reason-Custom-Actions-Screen bekommen eigene, aussagekräftigere
        // Namen. Andere Radio-/Checkbox-Screens (falls es die gibt)
        // bleiben generisch 'options_shown'.
        var isReasonScreen = isReasonSelectionScreenVisible(root);
        var resolvedEventName = isReasonScreen
          ? 'reasons_shown'
          : (isCustomActionsScreen
            ? (currentScreenIsRetentionOffer ? 'retention_offer_shown' : 'reason_custom_actions_shown')
            : 'options_shown');
        // Primärer Angebotsname = erster Button-Text, NUR wenn es
        // tatsächlich ein konkretes Angebot ist (nicht bei generischen
        // Custom Actions, die haben kein singuläres Angebot).
        if (currentScreenIsRetentionOffer) {
          currentOfferName = options[0] || null;
        }
        emitNormalizedEvent({
          event_name: resolvedEventName,
          step_name: getStepName(),
          options_shown: JSON.stringify(options.slice(0, 50)),
          // Angebotsname (nur bei echtem Retention-Angebot befüllt) -
          // NICHT mehr der Grund, siehe reason_selected dafür.
          offer_viewed: currentScreenIsRetentionOffer ? currentOfferName : null,
          // Der zuletzt angeklickte Kündigungsgrund (aus der Reasons-
          // Liste) im Klartext - eigenes Feld, nicht mehr in offer_viewed
          // vermischt.
          reason_selected: isCustomActionsScreen ? lastClickedReasonText : null,
          // Von incentive/inspect gelieferte echte Reason-ID - nur EIN
          // Event für diesen Moment, angereichert statt dupliziert.
          cancellation_reason: currentScreenIsRetentionOffer ? pendingRetentionOfferReasonId : null,
          // Welche Benefit-Page/Konfiguration galt, als diese Reasons
          // gezeigt wurden - Kontext, der sonst mit dem unterdrückten
          // benefit_response verloren ginge.
          _benefit_needs_product_context: isReasonScreen,
          source: 'portal_frontend_dom',
        });
      } catch (e) {
        // silent fail
      }
    }, CONFIG.OPTIONS_SNAPSHOT_DEBOUNCE_MS);
  }

  function startObserving() {
    try {
      var observer = new MutationObserver(function () {
        checkDrawerLifecycle();
        scheduleOptionsSnapshot();
      });
      observer.observe(document.body, { childList: true, subtree: true });
      checkDrawerLifecycle();
      scheduleOptionsSnapshot();
    } catch (e) {
      // silent fail
    }
  }

  if (document.body) {
    startObserving();
  } else {
    document.addEventListener('DOMContentLoaded', startObserving);
  }

  // -- B3: Freitext-Nutzung (NUR Textareas, mit Blacklist-Schutz) -------

  function isExcludedField(el) {
    try {
      var haystack = [
        el.name || '', el.id || '', el.getAttribute('autocomplete') || '',
        el.type || '', el.getAttribute('aria-label') || '',
      ].join(' ');
      for (var i = 0; i < CONFIG.TEXT_INPUT_EXCLUDE_PATTERNS.length; i++) {
        if (CONFIG.TEXT_INPUT_EXCLUDE_PATTERNS[i].test(haystack)) return true;
      }
      return false;
    } catch (e) {
      return true; // im Zweifel ausschließen
    }
  }

  var textInputStartedKeys = {};

  document.addEventListener('focusin', function (e) {
    try {
      var el = e.target;
      if (el.tagName !== 'TEXTAREA') return;
      var root = getFlowRoot();
      if (!root || !root.contains(el)) return;
      if (isExcludedField(el)) return;
      var key = getElementId(el) || 'textarea_' + SESSION_ID;
      if (textInputStartedKeys[key]) return;
      textInputStartedKeys[key] = true;
      emitNormalizedEvent({
        event_name: 'text_input_started',
        step_name: getStepName(),
        element_class: el.className || null,
        _benefit_needs_product_context: isBenefitPageVisible(root),
        source: 'portal_frontend_dom',
      });
    } catch (e) {
      // silent fail
    }
  }, true);

  document.addEventListener('focusout', function (e) {
    try {
      var el = e.target;
      if (el.tagName !== 'TEXTAREA') return;
      var root = getFlowRoot();
      if (!root || !root.contains(el)) return;
      if (isExcludedField(el)) return;
      var value = (el.value || '').trim();
      if (!value) return;
      emitNormalizedEvent({
        event_name: 'text_input_provided',
        step_name: getStepName(),
        element_class: el.className || null,
        text_input: value.slice(0, CONFIG.MAX_TEXT_LENGTH),
        _benefit_needs_product_context: isBenefitPageVisible(getFlowRoot()),
        source: 'portal_frontend_dom',
      });
    } catch (e) {
      // silent fail
    }
  }, true);

  function isDrawerPresent() {
    try { return !!document.querySelector('.loop-drawer-content, .loop-drawer'); } catch (e) { return false; }
  }

  // -- B4b: Drawer-Lebenszyklus - feuert 'flow_closed', sobald .loop-drawer
  //         aus dem DOM verschwindet (X-Klick, ESC, Klick daneben, oder
  //         nach erfolgreicher Aktion). Unabhängig vom Grund - Ausgang
  //         (gekündigt/gerettet/einfach weggeklickt) ergibt sich erst im
  //         Abgleich mit den anderen Events derselben session_id.
  // Zusätzliches Sicherheitsnetz für 'flow_opened' (Fund von Babett,
  // 17.09.): hängt bisher NUR am subscriptionBenefit/inspect-Call - bei
  // Selling-Plänen ohne zugeordnete Benefit-Page ruft Loop diesen Call
  // vermutlich gar nicht auf, der Kunde springt über einen anderen,
  // unbekannten Call direkt zur Reasons-Liste. DOM-Fallback erkennt den
  // Einstieg unabhängig davon, WELCHER API-Call ihn ausgelöst hat.
  var apiFlowOpenedFired = false;
  var drawerWasOpen = false;
  function checkDrawerLifecycle() {
    try {
      var present = isDrawerPresent();
      if (present && !drawerWasOpen) {
        // Drawer ist gerade NEU aufgetaucht - kurz warten (das API-Signal
        // ist meist nahezu zeitgleich), dann nur feuern, wenn das
        // API-bestätigte flow_opened bis dahin NICHT gekommen ist.
        setTimeout(function () {
          if (!apiFlowOpenedFired) {
            emitNormalizedEvent({
              event_name: 'flow_opened',
              step_name: getStepName(),
              _benefit_needs_product_context: true,
              source: 'portal_frontend_dom_fallback',
            });
          }
        }, 1200);
      }
      if (present) {
        drawerWasOpen = true;
      } else if (drawerWasOpen) {
        drawerWasOpen = false;
        apiFlowOpenedFired = false; // zurücksetzen für einen möglichen erneuten Einstieg in derselben Sitzung
        emitNormalizedEvent({
          event_name: 'flow_closed',
          step_name: lastKnownStepName,
          source: 'portal_frontend_dom',
        });
      }
    } catch (e) {
      // silent fail
    }
  }

  // -- B4c: Abbruch-Erkennung (Tab verlassen / Fenster schließen) --------
  // Feuert NUR, wenn der Drawer zu diesem Zeitpunkt noch offen war - ein
  // kurzer Tab-Wechsel NACH regulärem Abschluss/Schließen zählt nicht als
  // Abbruch.

  var abandonSent = false;
  function emitAbandon() {
    if (abandonSent) return;
    if (!isDrawerPresent()) return; // Flow war schon regulär beendet/geschlossen
    abandonSent = true;
    try {
      emitNormalizedEvent({
        event_name: 'portal_abandoned',
        step_name: getStepName(),
        _benefit_needs_product_context: isBenefitPageVisible(getFlowRoot()),
        source: 'portal_frontend_dom',
      });
    } catch (e) {
      // silent fail
    }
  }

  window.addEventListener('beforeunload', emitAbandon);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') emitAbandon();
  });

  // -- ECHTES "portal_opened": Kunde ist auf der Abo-Detailseite,
  //    unabhängig davon ob er je "Abo kündigen" klickt. Feuert einmalig,
  //    sobald loopProps beim Seitenaufruf verfügbar wird.
  //    KEIN benefit_page_viewed hier - der Kunde hat die Benefit-Page an
  //    dieser Stelle noch nicht gesehen, nur welche es WÄRE, falls er
  //    auf "Abo kündigen" klickt. Das würde sonst fälschlich so aussehen,
  //    als hätte er sie bereits gesehen.
  getIdentity(function () {
    emitNormalizedEvent({
      event_name: 'portal_opened',
      source: 'portal_frontend',
    });
  });

  // -- DEBUG: temporäres Werkzeug zum Live-Nachschauen in der Console ---
  // window.__fmtDebug.getFlowRoot() etc. - hilft, ohne weitere
  // Rateschleifen direkt zu sehen, was die interne Erkennung liefert.
  window.__fmtDebug = {
    getFlowRoot: getFlowRoot,
    isBenefitPageVisible: function () { return isBenefitPageVisible(getFlowRoot()); },
    isReasonCustomActionsVisible: function () { return isReasonCustomActionsVisible(getFlowRoot()); },
    isReasonSelectionScreenVisible: function () { return isReasonSelectionScreenVisible(getFlowRoot()); },
    isFeedbackScreenVisible: function () { return isFeedbackScreenVisible(getFlowRoot()); },
    getStepName: getStepName,
    getDrawerIds: function () {
      return Array.prototype.map.call(document.querySelectorAll('.loop-drawer'), function (d) { return d.id; });
    },
    lastOptionsKey: function () { return lastOptionsKey; },
    lastClickedReasonText: function () { return lastClickedReasonText; },
    currentScreenIsRetentionOffer: function () { return currentScreenIsRetentionOffer; },
    lastKnownBenefitId: function () { return lastKnownBenefitId; },
    currentOfferName: function () { return currentOfferName; },
    // Manuell testen: window.__fmtDebug.testScreenDetection('Benefit Page', window.__fmtDebug.isBenefitPageVisible)
    testScreenDetection: scheduleScreenDetectionCheck,
  };
})();